import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  createSalesPerson,
  deleteSalesPerson,
  getSalesPerson,
  getSalesPersonById,
  updateSalesPerson,
  approveSalesPerson,
  activateSalesPerson,
  getUpcomingTrainingSessions,
  completeTraining,
  getSalespersonProfile,
  createSalespersonQuote,
  sendSalespersonQuoteEmail,
} from "../../../services";
import { errorToast, successToast } from "../../../services/toasts";
import { fetchCityOptions } from "../../../supplierCheckout/services/getCities";
import { CalendlyResponse } from "../../../types/training";
import { QUERY_KEYS } from "../queryKeys";

// ============================================================
// SALES PERSON QUERIES
// ============================================================

export const useGetSalesPerson = () => {
  return useQuery<SalesPeople, Error>({
    queryKey: [QUERY_KEYS.GET_SALES_PERSON],
    queryFn: () => getSalesPerson(),
    refetchOnWindowFocus: false,
  });
};

export const useGetSalespersonCities = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_CITIES],
    queryFn: () => fetchCityOptions(),
    refetchOnWindowFocus: false,
  });
};

export const useGetSalesPersonById = (id: string) => {
  return useQuery<SalesPeople, Error>({
    queryKey: [QUERY_KEYS.GET_SALES_PERSON_BY_ID, id],
    queryFn: () => getSalesPersonById(id),
    enabled: !!id,
  });
};

export const useGetSalespersonProfile = () => {
  return useQuery<SalesPeople, Error>({
    queryKey: [QUERY_KEYS.GET_SALESPERSON_PROFILE],
    queryFn: () => getSalespersonProfile(),
    refetchOnWindowFocus: false,
  });
};

export const useCreateSalesPerson = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      salesPersonData,
      companyName,
    }: {
      salesPersonData: FormData;
      companyName?: string;
    }) => createSalesPerson(salesPersonData, companyName!),
    onSuccess: (data) => {
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_SALES_PERSON_BY_COMPANYNAME,
      ]);

      // Use the message from backend if available, otherwise use default
      const message = data?.message || "Solicitud de asesor enviada con éxito";
      successToast(message);
    },
    onError: (error: Error) => {
      const errorMessage =
        error.message || "Error al enviar la solicitud del asesor";
      errorToast(errorMessage);
    },
  });
};

export const useUpdateSalesPerson = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      salesPersonId,
      salesPersonData,
    }: {
      salesPersonId: number;
      salesPersonData: any;
    }) => updateSalesPerson(salesPersonId, salesPersonData),
    onSuccess: () => {
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_SALES_PERSON_BY_COMPANYNAME,
      ]);

      successToast("El asesor ha sido modificado con éxito");
    },
    onError: () => {
      errorToast("Error al actualizar el asesor");
    },
  });
};

export const useDeleteSalesPerson = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (salesPersonId: number) => deleteSalesPerson(salesPersonId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_SALES_PERSON],
      });
      successToast("El asesor ha sido eliminado con éxito");
    },
    onError: () => {
      errorToast("Error al eliminar el asesor");
    },
  });
};

export const useApproveSalesPerson = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      salesPersonId,
      salesPersonData,
    }: {
      salesPersonId: number;
      salesPersonData: any;
    }) => approveSalesPerson(salesPersonId, salesPersonData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_SALES_PERSON],
      });
      successToast("El asesor ha sido modificado con éxito");
    },
    onError: () => {
      errorToast("Error al modificar el asesor");
    },
  });
};

export const useActivateSalesPerson = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      salesPersonId,
      salesPersonData,
    }: {
      salesPersonId: number;
      salesPersonData: any;
    }) => activateSalesPerson(salesPersonId, salesPersonData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_SALES_PERSON],
      });
      successToast("El asesor ha sido modificado con éxito");
    },
    onError: () => {
      errorToast("Error al modificar el asesor");
    },
  });
};

export const useCreateSalespersonQuote = () => {
  return useMutation({
    mutationFn: (payload: { quotation: any; totals: any }) =>
      createSalespersonQuote(payload),
    onSuccess: (data) => {
      const message = data?.message || "Cotizacion enviada con exito";
      successToast(message);
    },
    onError: (error: Error) => {
      const errorMessage = error.message || "Error al enviar la cotizacion";
      errorToast(errorMessage);
    },
  });
};

export const useSendSalespersonQuoteEmail = () => {
  return useMutation({
    mutationFn: (payload: {
      pdfUrl: string;
      clientEmail: string;
      clientName: string;
      advisorEmail: string;
    }) => sendSalespersonQuoteEmail(payload),
    onSuccess: (data) => {
      const message = data?.message || "Correo enviado con exito";
      successToast(message);
    },
    onError: (error: Error) => {
      const errorMessage = error.message || "Error al enviar el correo";
      errorToast(errorMessage);
    },
  });
};

// ============================================================
// TRAINING SESSIONS QUERIES
// ============================================================

export const useGetTrainingSessions = () => {
  return useQuery<CalendlyResponse, Error>({
    queryKey: [QUERY_KEYS.GET_TRAINING_SESSIONS],
    queryFn: () => getUpcomingTrainingSessions(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: () => {
      errorToast("Error al cargar las capacitaciones");
    },
  });
};

export const useCompleteTraining = () => {
  return useMutation({
    mutationFn: () => completeTraining(),
    onSuccess: () => {
      successToast("¡Capacitaciones finalizadas exitosamente!");
    },
    onError: () => {
      errorToast("Error al finalizar las capacitaciones");
    },
  });
};
